import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import EnhanceButton from './EnhanceButton';
import TextareaWithEnhance from './TextareaWithEnhance';
import './Create.css';
import enhanceText from '../../api/enhanceText';

const Create = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [solution, setSolution] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [category, setCategory] = useState('');
  const [difficulty, setDifficulty] = useState('中等');
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [summary, setSummary] = useState('');
  
  // 添加新的状态
  const [enhancingType, setEnhancingType] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [enhancedResult, setEnhancedResult] = useState('');
  const [originalText, setOriginalText] = useState('');
  
  // 添加refs用于访问TextareaWithEnhance组件
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const contentRef = useRef(null);
  const solutionRef = useRef(null);
  const summaryRef = useRef(null);
  
  // 添加滚动到顶部效果
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // 检查当前正在编辑的是哪个字段，方便调试
  const getDebugInfo = () => {
    console.log("当前refs状态:", {
      titleRef: titleRef?.current ? "已初始化" : "未初始化",
      contentRef: contentRef?.current ? "已初始化" : "未初始化",
      summaryRef: summaryRef?.current ? "已初始化" : "未初始化",
      descriptionRef: descriptionRef?.current ? "已初始化" : "未初始化",
      solutionRef: solutionRef?.current ? "已初始化" : "未初始化",
    });
  };
  
  // 在组件加载后检查refs状态
  useEffect(() => {
    // 延迟检查，确保refs已经设置
    const timer = setTimeout(() => {
      getDebugInfo();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // 处理AI润色
  const handleEnhance = async (contentType) => {
    console.log(`润色请求类型: ${contentType}`);
    getDebugInfo();
    
    let contentToEnhance = '';
    let textareaRef = null;
    
    if (contentType === 'title') {
      contentToEnhance = title;
      textareaRef = titleRef;
    } else if (contentType === 'content') {
      contentToEnhance = content;
      textareaRef = contentRef;
    } else if (contentType === 'summary') {
      contentToEnhance = summary;
      textareaRef = summaryRef;
    } else if (contentType === 'description') {
      contentToEnhance = description;
      textareaRef = descriptionRef;
    } else if (contentType === 'solution') {
      contentToEnhance = solution;
      textareaRef = solutionRef;
    } else {
      return;
    }
    
    // 检查ref是否存在
    if (!textareaRef || !textareaRef.current) {
      console.error(`TextareaRef for ${contentType} is not initialized`);
      alert(`润色功能暂时不可用，请刷新页面后重试`);
      return;
    }
    
    // 允许空内容，AI会生成全新的内容
    if (contentToEnhance.trim() === '') {
      contentToEnhance = '请为我生成一段合适的内容';
    }
    
    // 保存原始文本，以便拒绝时恢复
    setOriginalText(contentToEnhance);
    setEnhancingType(contentType);
    
    // 开始润色动画
    textareaRef.current.startEnhancingAnimation();
    
    try {
      // 使用通义千问API进行润色
      const response = await enhanceText(contentToEnhance);
      
      if (response.success) {
        // 处理成功响应 - 标记为增强状态，但不立即更新状态
        setEnhancedResult(response.data);
        
        // 显示增强后的文本，但不立即更新状态
        textareaRef.current.startRenderEnhancedTextWithConfirmation(
          response.data, 
          () => handleAcceptEnhancement(), 
          () => handleRejectEnhancement()
        );
      } else {
        // 处理错误
        alert(response.error || '润色失败，请稍后再试');
        textareaRef.current.hideEnhancingPlaceholder();
        setEnhancingType('');
      }
    } catch (error) {
      console.error('润色过程出错:', error);
      alert('润色服务暂时不可用，请稍后再试');
      textareaRef.current.hideEnhancingPlaceholder();
      setEnhancingType('');
    }
  };
  
  // 处理接受润色结果
  const handleAcceptEnhancement = () => {
    // 根据正在处理的类型更新相应的状态
    if (enhancingType === 'title') {
      setTitle(enhancedResult);
      if (titleRef && titleRef.current) {
        titleRef.current.restoreOriginalText(enhancedResult);
      }
    } else if (enhancingType === 'content') {
      setContent(enhancedResult);
      if (contentRef && contentRef.current) {
        contentRef.current.restoreOriginalText(enhancedResult);
      }
    } else if (enhancingType === 'summary') {
      setSummary(enhancedResult);
      if (summaryRef && summaryRef.current) {
        summaryRef.current.restoreOriginalText(enhancedResult);
      }
    } else if (enhancingType === 'description') {
      setDescription(enhancedResult);
      if (descriptionRef && descriptionRef.current) {
        descriptionRef.current.restoreOriginalText(enhancedResult);
      }
    } else if (enhancingType === 'solution') {
      setSolution(enhancedResult);
      if (solutionRef && solutionRef.current) {
        solutionRef.current.restoreOriginalText(enhancedResult);
      }
    }
    
    // 重置状态
    setEnhancedResult('');
    setEnhancingType('');
    setOriginalText('');
  };
  
  // 处理拒绝润色结果
  const handleRejectEnhancement = () => {
    // 如果拒绝，则需要将原文本恢复到对应的textarea
    if (enhancingType === 'title') {
      setTitle(originalText);
      if (titleRef && titleRef.current) {
        titleRef.current.restoreOriginalText(originalText);
      }
    } else if (enhancingType === 'content') {
      setContent(originalText);
      if (contentRef && contentRef.current) {
        contentRef.current.restoreOriginalText(originalText);
      }
    } else if (enhancingType === 'summary') {
      setSummary(originalText);
      if (summaryRef && summaryRef.current) {
        summaryRef.current.restoreOriginalText(originalText);
      }
    } else if (enhancingType === 'description') {
      setDescription(originalText);
      if (descriptionRef && descriptionRef.current) {
        descriptionRef.current.restoreOriginalText(originalText);
      }
    } else if (enhancingType === 'solution') {
      setSolution(originalText);
      if (solutionRef && solutionRef.current) {
        solutionRef.current.restoreOriginalText(originalText);
      }
    }
    
    // 重置状态
    setEnhancedResult('');
    setEnhancingType('');
    setOriginalText('');
  };
  
  // 分类选项 - 与首页保持一致，移除"其他"选项
  const categories = [
    { value: '推理', label: '推理' },
    { value: '悬疑', label: '悬疑' },
    { value: '恐怖', label: '恐怖' },
    { value: '奇幻', label: '奇幻' },
    { value: '科幻', label: '科幻' },
    { value: '冒险', label: '冒险' },
    { value: '生活', label: '生活' }
  ];
  
  // 难度选项
  const difficulties = ['简单', '中等', '困难'];
  
  // 新增状态用于自定义下拉框
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // 处理封面图片上传
  const handleCoverUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !description || !content || !solution || !category) {
      alert('请填写所有必填字段');
      return;
    }
    
    setIsSubmitting(true);
    
    // 构建表单数据
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('content', content);
    formData.append('solution', solution);
    formData.append('approvalReason', approvalReason);
    formData.append('category', category);
    formData.append('difficulty', difficulty);
    if (coverImage) {
      formData.append('cover', coverImage);
    }
    
    try {
      // 模拟API调用
      console.log('提交的数据:', {
        title,
        description,
        content,
        solution,
        approvalReason,
        category,
        difficulty,
        hasCover: !!coverImage
      });
      
      // 实际项目中应该有实际的API调用
      // const response = await api.createPuzzle(formData);
      
      // 模拟成功响应
      setTimeout(() => {
        alert('海龟汤创建成功！');
        setIsSubmitting(false);
        // 清空表单
        setTitle('');
        setDescription('');
        setContent('');
        setSolution('');
        setApprovalReason('');
        setCategory('');
        setDifficulty('中等');
        setCoverImage(null);
        setCoverPreview('');
      }, 1500);
      
    } catch (error) {
      console.error('创建失败:', error);
      alert('创建失败，请稍后重试');
      setIsSubmitting(false);
    }
  };

  // 修改封面上传区域的样式
  const coverStyleOverride = {
    width: '300px', // 固定宽度300px，正方形
    height: '300px', // 保持高度300px
    margin: '0', // 左对齐
    textAlign: 'left', // 文字左对齐
    marginTop: '0px' // 不移动（比之前上移3px）
  };

  // 封面预览图片的样式
  const coverPreviewStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover' // 确保图片填充整个区域
  };

  // 获取指定类型的按钮旁边状态文本
  const getEnhancingStatus = (type) => {
    if (isEnhancing && enhancingType === type) {
      return <span className="enhancing-status">正在润色中</span>;
    }
    return null;
  };

  return (
    <div className="elegant-app">
      <Header />
      
      <main className="elegant-main">
        <div className="elegant-create-container">
          <div className="elegant-create-header">
            <h1 className="elegant-create-title">创作海龟汤</h1>
            <p className="elegant-create-subtitle">创作一个引人入胜的谜题，让参与者尽情解谜</p>
          </div>
          
          <form className="elegant-create-form" onSubmit={handleSubmit}>
            <div className="elegant-form-grid">
              <div className="elegant-form-left">
                <div className="elegant-form-group">
                  <label className="elegant-label" style={{ marginBottom: '8px' }}>
                    标题
                    <span className="required">*</span>
                  </label>
                  <TextareaWithEnhance
                    id="title" 
                    className="elegant-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="请输入标题..."
                    rows={1}
                    required={true}
                    onEnhance={() => handleEnhance('title')}
                    ref={titleRef}
                  />
                </div>
                
                <div className="elegant-form-group">
                  <div className="elegant-label-row" style={{ marginBottom: '8px' }}>
                    <label className="elegant-label" style={{ marginBottom: '0' }}>
                      简介
                      <span className="required">*</span>
                    </label>
                    <div className="enhance-button-container">
                      {getEnhancingStatus('description')}
                      <button 
                        className="enhance-magic-button"
                        onClick={() => handleEnhance('description')}
                        disabled={!description.trim() || isEnhancing}
                        title="AI 润色"
                      >
                        <i className="fas fa-magic"></i>
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: '0' }}>
                  <TextareaWithEnhance 
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="请输入简介..."
                    maxLength={100}
                    rows={3}
                    required={true}
                    onEnhance={() => handleEnhance('description')}
                    ref={descriptionRef}
                  />
                  </div>
                </div>
                
                <div className="elegant-form-row">
                  <div className="elegant-form-group">
                    <label htmlFor="category" className="elegant-label">分类 <span className="required">*</span></label>
                    <div className="elegant-custom-select">
                      <div 
                        className="elegant-select-header" 
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      >
                        <span>{category || "选择分类"}</span>
                        <svg viewBox="0 0 24 24" width="18" height="18">
                          <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                        </svg>
                      </div>
                      {isDropdownOpen && (
                        <div className="elegant-select-options">
                          {categories.map(cat => (
                            <div 
                              key={cat.value} 
                              className={`elegant-select-option ${category === cat.value ? 'active' : ''}`}
                              onClick={() => {
                                setCategory(cat.value);
                                setIsDropdownOpen(false);
                              }}
                            >
                              {cat.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="elegant-form-group">
                    <label htmlFor="difficulty" className="elegant-label">难度</label>
                    <div className="elegant-difficulty-selector">
                      {difficulties.map(diff => (
                        <button
                          key={diff}
                          type="button"
                          className={`elegant-difficulty-btn ${difficulty === diff ? 'active' : ''} ${diff === '简单' ? 'easy' : diff === '中等' ? 'medium' : 'hard'}`}
                          onClick={() => setDifficulty(diff)}
                        >
                          {diff}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="elegant-form-group" style={{ marginBottom: '-5px' }}>
                  <label htmlFor="cover" className="elegant-label">封面图片</label>
                  <div className="elegant-cover-upload" style={coverStyleOverride}>
                    {coverPreview ? (
                      <div className="elegant-cover-preview" style={{ width: '100%', height: '100%', position: 'relative' }}>
                        <img 
                          src={coverPreview} 
                          alt="封面预览" 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover', 
                            display: 'block'
                          }} 
                        />
                        <label htmlFor="cover-input" style={{
                          position: 'absolute',
                          top: '0',
                          left: '0',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(0, 0, 0, 0)',
                          color: 'rgba(255, 255, 255, 0)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          zIndex: 2,
                          gap: '10px'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
                          e.currentTarget.style.color = 'rgba(255, 255, 255, 1)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 0, 0, 0)';
                          e.currentTarget.style.color = 'rgba(255, 255, 255, 0)';
                        }}
                        >
                          <svg viewBox="0 0 24 24" width="24" height="24">
                            <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z" fill="currentColor"/>
                          </svg>
                          <span style={{ fontWeight: 'bold' }}>更换封面</span>
                          <input 
                            type="file" 
                            id="cover-input" 
                            accept="image/*"
                            onChange={handleCoverUpload}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    ) : (
                      <label htmlFor="cover-input" className="elegant-cover-label">
                        <svg viewBox="0 0 24 24" width="24" height="24">
                          <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z" fill="currentColor"/>
                        </svg>
                        <span>上传封面图片</span>
                        <input 
                          type="file" 
                          id="cover-input" 
                          accept="image/*"
                          onChange={handleCoverUpload}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="elegant-form-right">
                <div className="elegant-form-group">
                  <div className="elegant-label-row" style={{ marginBottom: '8px' }}>
                    <label className="elegant-label" style={{ marginBottom: '0' }}>
                      海龟汤 汤面
                      <span className="required">*</span>
                    </label>
                    <div className="enhance-button-container">
                      {getEnhancingStatus('content')}
                      <button 
                        className="enhance-magic-button"
                        onClick={() => handleEnhance('content')}
                        disabled={!content.trim() || isEnhancing}
                        title="AI 润色"
                      >
                        <i className="fas fa-magic"></i>
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: '0' }}>
                  <TextareaWithEnhance 
                    id="content"
                    className="elegant-textarea large"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="请输入内容..."
                    rows={8}
                    required={true}
                    onEnhance={() => handleEnhance('content')}
                    ref={contentRef}
                  />
                  </div>
                </div>
                
                <div className="elegant-form-group">
                  <div className="elegant-label-row" style={{ marginBottom: '8px' }}>
                    <label className="elegant-label" style={{ marginBottom: '0' }}>
                      海龟汤 汤底
                      <span className="required">*</span>
                    </label>
                    <div className="enhance-button-container">
                      {getEnhancingStatus('solution')}
                      <button 
                        className="enhance-magic-button"
                        onClick={() => handleEnhance('solution')}
                        disabled={!solution.trim() || isEnhancing}
                        title="AI 润色"
                      >
                        <i className="fas fa-magic"></i>
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: '0' }}>
                  <TextareaWithEnhance 
                    id="solution"
                    className="elegant-textarea large"
                    value={solution}
                    onChange={(e) => setSolution(e.target.value)}
                      placeholder="请输入解答..."
                    rows={8}
                    required={true}
                    onEnhance={() => handleEnhance('solution')}
                      ref={solutionRef}
                  />
                  </div>
                </div>
                
                <div className="elegant-form-group">
                  <label htmlFor="approvalReason" className="elegant-label">审核通过理由</label>
                  <TextareaWithEnhance 
                    id="approvalReason" 
                    className="elegant-textarea large"
                    value={approvalReason}
                    onChange={(e) => setApprovalReason(e.target.value)}
                    placeholder="写出您认为这个海龟汤应该通过审核的理由（最多200字）。写了更有可能通过审核。"
                    maxLength={200}
                    rows={7}
                    isApprovalReason={true}
                  />
                </div>
              </div>
            </div>
            
            <div className="elegant-submit-container">
              <Link to="/" className="elegant-cancel-btn">取消</Link>
              <button 
                type="button" 
                className="elegant-save-btn"
                onClick={() => {
                  // 保存草稿逻辑
                  alert('保存草稿功能已触发');
                }}
              >
                保存
              </button>
              <button 
                type="submit" 
                className="elegant-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? '提交中' : '发布'}
              </button>
            </div>
          </form>
          
          <div className="elegant-create-tips">
            <h3>创作小技巧：</h3>
            <ul>
              <li>好的海龟汤应该有吸引人的情境和巧妙的转折</li>
              <li>谜题要有一定的挑战性，但解答应该符合逻辑</li>
              <li>尝试设计能够引导读者一步步接近真相的问题</li>
              <li>确保你的解答全面地解释了谜题中所有看似矛盾的地方</li>
            </ul>
          </div>
        </div>
      </main>
      
      <Footer />
      
      {/* 润色结果预览对话框 */}
      {showPreview && (
        <div className="enhance-preview-overlay">
          <div className="enhance-preview-dialog">
            <div className="enhance-preview-header">
              <h3>AI润色结果预览</h3>
            </div>
            <div className="enhance-preview-content">
              <div className="enhance-comparison">
                <div className="enhance-original">
                  <h4>原文</h4>
                  <div className="text-content">{originalText}</div>
                </div>
                <div className="enhance-result">
                  <h4>润色后</h4>
                  <div className="text-content">{enhancedResult}</div>
                </div>
              </div>
            </div>
            <div className="enhance-preview-actions">
              <button 
                className="enhance-reject-btn"
                onClick={handleRejectEnhancement}
              >
                拒绝
              </button>
              <button 
                className="enhance-accept-btn"
                onClick={handleAcceptEnhancement}
              >
                接受
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Create;